import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { slotToLocalDateTime } from "src/Utils/datetime";
import {
  AnonymousBookingDraft,
  Booking,
  BookingStatus,
} from "src/business/booking";
import { DayHoursList } from "src/business/restaurant/restaurant";
import {
  useBookingsList,
  useBookingsStatusUpdate,
  useCreateAnonymousBooking,
  useUpdateBookingTable,
} from "src/data/booking/queries/booking";
import { useUpdateNumberOfTables } from "src/data/restaurant/queries/restaurant";
import { CreateBookingModal } from "src/components/Main/Pages/Bookings/components/CreateBookingModal";
import { findDealForSlot, offerForDeal } from "../dealHelpers";
import { useGetRestaurantDeals } from "src/data/deals/queries/deals";
import { CircularLoader } from "src/design/CircularLoader";
import { SnackBar } from "src/design";
import {
  colorBackgroundInfo,
  colorBorderInfo,
  colorTextMuted,
  radiusCard,
} from "src/design/tokens";
import {
  BookingSegment,
  activeDayView,
  bookingIntervals,
  buildDaySegments,
  dealsByRow,
  getUnassignedBookings,
  hasTableConflict,
  openWindowsForDate,
  resolveDropStartRow,
  rowSpanForDuration,
  slotLabelForRow,
  SLOTS_PER_DAY,
  tableColumns,
} from "../dayGridHelpers";
import { GridPoint, useGridDrag } from "../useGridDrag";
import { DayGrid } from "./DayGrid";
import { DropPreview } from "./TableColumn";
import { ScheduleHeader } from "./ScheduleHeader";
import { BoardLegend } from "./BoardLegend";
import { UnassignedTray } from "./UnassignedTray";
import { BookingInspector } from "./BookingInspector";

interface TableScheduleBoardProps {
  restaurantId: string;
  numberOfTables: number;
  bookingDuration: number;
  schedule: DayHoursList;
  leaves: string[];
  canUpdate: boolean;
  canCreate: boolean;
  /** Re-reads the restaurant so a table added or removed redraws the columns. */
  onNumberOfTablesChange: () => void;
  /** True while that re-read is in flight — `numberOfTables` is stale until it lands. */
  isRefreshingDetails: boolean;
}

export const TableScheduleBoard = ({
  restaurantId,
  numberOfTables,
  bookingDuration,
  schedule,
  leaves,
  canUpdate,
  canCreate,
  onNumberOfTablesChange,
  isRefreshingDetails,
}: TableScheduleBoardProps) => {
  const { t } = useTranslation();

  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [modalOpen, setModalOpen] = useState(false);
  const [draftBooking, setDraftBooking] = useState<AnonymousBookingDraft | null>(
    null,
  );
  const [inspectedId, setInspectedId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Slot chosen by clicking an empty cell; the modal collects the guest details
  // and the booking is created straight onto it.
  const [pendingSlot, setPendingSlot] = useState<GridPoint | null>(null);

  const columnsRef = useRef<HTMLDivElement>(null);
  const trayRef = useRef<HTMLDivElement>(null);

  // A whole day of slack on each side. The previous day is needed for the tail
  // of a booking running past midnight, and the range is sent as epoch millis
  // while the backend compares against naive LocalDateTimes — so any timezone
  // offset shifts the window and clips bookings near the day's edges. Over-
  // fetching costs one day either way; `buildDaySegments` filters exactly.
  const startDate = selectedDate.subtract(1, "day").startOf("day").valueOf();
  const endDate = selectedDate.add(1, "day").endOf("day").valueOf();

  const {
    data: bookings = [],
    isLoading,
    refetch,
  } = useBookingsList({ startDate, endDate, restaurantId });

  // The deals endpoint ignores any date range and returns the restaurant's full
  // list, so fetch once and resolve per-date applicability on the client.
  const { data: deals = [] } = useGetRestaurantDeals({ restaurantId });

  const { mutate: updateBookingTable } = useUpdateBookingTable(restaurantId);
  const { mutate: updateStatus, isPending: isUpdatingStatus } =
    useBookingsStatusUpdate(restaurantId, () => {
      setInspectedId(null);
      refetch();
    });
  const { mutate: createBooking } = useCreateAnonymousBooking(() => {
    setDraftBooking(null);
    refetch();
  });

  // Removing a table frees every future booking that was sitting on it, so the
  // board is re-read as well as the restaurant — the freed ones land in the tray.
  const { mutate: updateNumberOfTables, isPending: isUpdatingTables } =
    useUpdateNumberOfTables(restaurantId, () => {
      onNumberOfTablesChange();
      refetch();
    });

  const handleTableCountChange = useCallback(
    (next: number) => {
      updateNumberOfTables(next, {
        onError: () => setErrorMessage(t("tableSchedule.tableCount.error")),
      });
    },
    [updateNumberOfTables, t],
  );

  // Local mirror so a move lands instantly; the server stays the source of truth
  // and overwrites this on every refetch.
  const [localBookings, setLocalBookings] = useState<Booking[]>(bookings);
  useEffect(() => setLocalBookings(bookings), [bookings]);

  const tables = useMemo(() => tableColumns(numberOfTables), [numberOfTables]);
  const totalRows = useMemo(
    () => rowSpanForDuration(bookingDuration),
    [bookingDuration],
  );
  const openWindows = useMemo(
    () => openWindowsForDate(schedule, selectedDate, leaves),
    [schedule, selectedDate, leaves],
  );
  const dealRows = useMemo(
    () => dealsByRow(deals, selectedDate),
    [deals, selectedDate],
  );
  const segments = useMemo(
    () => buildDaySegments(localBookings, selectedDate, totalRows),
    [localBookings, selectedDate, totalRows],
  );
  const unassigned = useMemo(
    () => getUnassignedBookings(localBookings, selectedDate),
    [localBookings, selectedDate],
  );
  // The rows the grid draws: trimmed to the tightest run that still covers every
  // booking, open service and deal/event on the day. A restaurant is shut for
  // most of the 48 half-hours, and giving those rows equal height squeezes the
  // service down to a sliver.
  const dayView = useMemo(
    () => activeDayView(segments, openWindows, dealRows),
    [segments, openWindows, dealRows],
  );

  // Rows each table is already holding, used to refuse a double booking.
  const intervals = useMemo(
    () => bookingIntervals(localBookings, selectedDate, totalRows),
    [localBookings, selectedDate, totalRows],
  );
  /** Rows a placement would occupy, relative to the selected day's 00:00. */
  const spanFor = useCallback(
    (startRow: number, date: Dayjs) => {
      const offsetDays = date
        .startOf("day")
        .diff(selectedDate.startOf("day"), "day");
      const start = offsetDays * SLOTS_PER_DAY + startRow;
      return { start, end: start + totalRows };
    },
    [selectedDate, totalRows],
  );

  const conflictsAt = useCallback(
    (table: number, startRow: number, date: Dayjs, ignoreBookingId?: string) => {
      const { start, end } = spanFor(startRow, date);
      return hasTableConflict(intervals, table, start, end, ignoreBookingId);
    },
    [intervals, spanFor],
  );

  const moveBooking = useCallback(
    (booking: Booking, table: number, startRow: number, date: Dayjs = selectedDate) => {
      const slot = slotLabelForRow(startRow);
      const bookingDate = slotToLocalDateTime(date, slot);
      if (booking.assignedTable === table && booking.bookingDate === bookingDate) {
        return;
      }
      // One table seats one booking at a time.
      if (conflictsAt(table, startRow, date, booking.id)) {
        setErrorMessage(t("tableSchedule.conflictError"));
        return;
      }

      // Re-attribute the booking to whatever deal or event covers the slot it
      // lands on, clearing the offer when the destination carries none.
      const offer = offerForDeal(
        findDealForSlot(deals, date.format("YYYY-MM-DD"), slot),
      );

      setLocalBookings((previous) =>
        previous.map((candidate) =>
          candidate.id === booking.id
            ? {
                ...candidate,
                assignedTable: table,
                bookingHour: slot,
                bookingDate,
                dealContent: offer.dealContent ?? null,
                eventContent: offer.eventContent ?? null,
              }
            : candidate,
        ),
      );

      updateBookingTable(
        {
          bookingID: booking.id,
          assignedTable: table,
          isAnonymous: booking.isAnonymous,
          bookingDate,
          dealContent: offer.dealContent ?? null,
          eventContent: offer.eventContent ?? null,
        },
        {
          // Re-sync either way: a move can land on a day the board is not
          // currently showing, and the optimistic mirror is dropped whenever the
          // selected date changes the query key.
          onSuccess: () => refetch(),
          onError: () => {
            setErrorMessage(t("tableSchedule.moveError"));
            refetch();
          },
        },
      );
    },
    [selectedDate, deals, conflictsAt, updateBookingTable, refetch, t],
  );

  // Dropping onto the tray frees the table without touching the booking's time.
  const handleDropToTray = useCallback(
    (bookingId: string) => {
      const booking = localBookings.find(
        (candidate) => candidate.id === bookingId,
      );
      if (!booking || booking.assignedTable === null) return;

      setLocalBookings((previous) =>
        previous.map((candidate) =>
          candidate.id === bookingId
            ? { ...candidate, assignedTable: null }
            : candidate,
        ),
      );
      updateBookingTable(
        {
          bookingID: booking.id,
          assignedTable: null,
          isAnonymous: booking.isAnonymous,
          dealContent: booking.dealContent,
          eventContent: booking.eventContent,
        },
        {
          onSuccess: () => refetch(),
          onError: () => {
            setErrorMessage(t("tableSchedule.moveError"));
            refetch();
          },
        },
      );
    },
    [localBookings, updateBookingTable, refetch, t],
  );

  const handleDrop = useCallback(
    (bookingId: string, point: GridPoint, grabOffsetRows: number) => {
      const booking = localBookings.find(
        (candidate) => candidate.id === bookingId,
      );
      if (!booking) return;
      moveBooking(
        booking,
        point.table,
        resolveDropStartRow(point.row, grabOffsetRows),
      );
    },
    [localBookings, moveBooking],
  );

  const { drag, beginDrag, consumeClickAfterDrag } = useGridDrag({
    tables,
    gridRef: columnsRef,
    view: dayView,
    trayRef,
    onDrop: handleDrop,
    onDropToTray: handleDropToTray,
  });

  const handleBlockPointerDown = useCallback(
    (event: React.PointerEvent, segment: BookingSegment) => {
      if (!canUpdate) return;
      beginDrag(event, segment.booking.id, segment.absStartRow);
    },
    [beginDrag, canUpdate],
  );

  // A tray booking has no position yet, so it lands exactly where it is dropped.
  const handleTrayPointerDown = useCallback(
    (event: React.PointerEvent, booking: Booking) => {
      if (!canUpdate) return;
      beginDrag(event, booking.id, null);
    },
    [beginDrag, canUpdate],
  );

  const handleBlockClick = useCallback(
    (segment: BookingSegment) => {
      if (consumeClickAfterDrag()) return;
      setInspectedId(segment.booking.id);
    },
    [consumeClickAfterDrag],
  );

  // Create a booking straight onto a slot, attributing any offer covering it.
  const createAt = useCallback(
    (draft: AnonymousBookingDraft, table: number, row: number) => {
      const slot = slotLabelForRow(row);
      const deal = findDealForSlot(deals, selectedDate.format("YYYY-MM-DD"), slot);
      createBooking({
        ...draft,
        date: slotToLocalDateTime(selectedDate, slot),
        assignedTableNumber: table,
        dealId: deal?.id ?? null,
        dealType: deal?.type ?? null,
      });
    },
    [createBooking, deals, selectedDate],
  );

  const handleSlotClick = useCallback(
    (table: number, row: number) => {
      // A slot already taken on this table cannot host a second booking.
      if (conflictsAt(table, row, selectedDate)) {
        setErrorMessage(t("tableSchedule.conflictError"));
        return;
      }
      // Placing a drafted booking takes precedence; otherwise the slot itself
      // opens the modal, pre-set to that table and time.
      if (draftBooking) {
        createAt(draftBooking, table, row);
        return;
      }
      if (!canCreate) return;
      setPendingSlot({ table, row });
      setModalOpen(true);
    },
    [draftBooking, createAt, canCreate, conflictsAt, selectedDate, t],
  );

  const handleSubmitDraft = useCallback(
    (draft: AnonymousBookingDraft) => {
      if (pendingSlot) {
        createAt(draft, pendingSlot.table, pendingSlot.row);
        setPendingSlot(null);
        return;
      }
      // Opened from the header with no slot chosen: fall back to click-to-place.
      setDraftBooking(draft);
    },
    [pendingSlot, createAt],
  );

  const handleRescheduleDate = useCallback(
    (segment: BookingSegment, date: Dayjs) => {
      // Keep the time of day and the table; only the calendar day moves. The
      // start row is normalised because a spill-over segment starts negative.
      const rowInDay =
        ((segment.absStartRow % SLOTS_PER_DAY) + SLOTS_PER_DAY) % SLOTS_PER_DAY;
      moveBooking(segment.booking, segment.table, rowInDay, date);
      setInspectedId(null);
      // Follow the booking so it stays in view after the move.
      setSelectedDate(date);
    },
    [moveBooking],
  );

  const handleCancelBooking = useCallback(
    (segment: BookingSegment) => {
      updateStatus(
        {
          bookingID: segment.booking.id,
          status: BookingStatus.CANCELED,
          isAnonymous: segment.booking.isAnonymous,
        },
        { onError: () => setErrorMessage(t("tableSchedule.inspector.cancelError")) },
      );
    },
    [updateStatus, t],
  );

  const handleConfirmBooking = useCallback(
    (segment: BookingSegment) => {
      updateStatus(
        {
          bookingID: segment.booking.id,
          status: BookingStatus.CONFIRMED,
          isAnonymous: segment.booking.isAnonymous,
        },
        { onError: () => setErrorMessage(t("tableSchedule.inspector.confirmError")) },
      );
    },
    [updateStatus, t],
  );

  const dropPreview: DropPreview | null =
    drag?.active && drag.point
      ? {
          table: drag.point.table,
          startRow: resolveDropStartRow(drag.point.row, drag.grabOffsetRows),
          rows: totalRows,
        }
      : null;

  // Both halves of a booking crossing midnight resolve to the same booking, so
  // the inspector is the same whichever half was clicked.
  const inspectedSegment =
    segments.find((segment) => segment.booking.id === inspectedId) ?? null;

  // An empty tray is dead weight under the grid, so it only appears once it holds
  // something. Dropping a booking back is how a table is freed, though, so with
  // the tray hidden the legend band — always on screen, directly above the grid —
  // stands in as the drop zone. Anything overlaying the grid instead would turn
  // its bottom rows into an unassign zone mid-drag, and anything inserted into
  // the flow would resize the grid under the pointer.
  const hasUnassigned = unassigned.length > 0;
  const isDragActive = !!drag?.active;
  const isLegendDropZone = !hasUnassigned && isDragActive;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: { xs: 0.75, sm: 1.25 },
        height: "100%",
        minHeight: 0,
      }}
    >
      <ScheduleHeader
        date={selectedDate}
        onDateChange={setSelectedDate}
        numberOfTables={numberOfTables}
        canUpdateTables={canUpdate}
        // Stays inert until the refreshed count arrives: stepping off a stale
        // `numberOfTables` would send the same value twice and silently swallow
        // the second click.
        isUpdatingTables={isUpdatingTables || isRefreshingDetails}
        onTableCountChange={handleTableCountChange}
        canCreate={canCreate}
        isPlacing={!!draftBooking}
        onCreateClick={() => setModalOpen(true)}
        onCancelPlacement={() => setDraftBooking(null)}
      />

      {draftBooking && (
        <Box
          sx={{
            flexShrink: 0,
            px: 1.5,
            py: 0.75,
            backgroundColor: colorBackgroundInfo,
            borderRadius: radiusCard,
          }}
        >
          <Typography sx={{ fontSize: 12.5, color: colorTextMuted }}>
            {t("tableSchedule.placement.instruction", {
              name: `${draftBooking.firstName} ${draftBooking.lastName}`,
            })}
          </Typography>
        </Box>
      )}

      <Box
        // Only stands in for the tray while the tray itself is not rendered.
        ref={hasUnassigned ? undefined : trayRef}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          flexShrink: 0,
          px: 0.75,
          // Always bordered, only ever tinted: colouring a transparent border
          // keeps the band exactly the same height whether or not it is a target.
          border: `1px dashed ${
            isLegendDropZone ? colorBorderInfo : "transparent"
          }`,
          borderRadius: radiusCard,
          transition: "border-color 120ms ease",
        }}
      >
        <BoardLegend />
        {isLegendDropZone && (
          <Typography
            sx={{
              fontSize: 11.5,
              fontStyle: "italic",
              color: colorTextMuted,
              whiteSpace: "nowrap",
            }}
          >
            {t("tableSchedule.unassigned.dropHere")}
          </Typography>
        )}
      </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8, flex: 1 }}>
          <CircularLoader />
        </Box>
      ) : (
        <DayGrid
          sx={{ flex: 1 }}
          tables={tables}
          segments={segments}
          openWindows={openWindows}
          dealRows={dealRows}
          view={dayView}
          isPlacing={!!draftBooking}
          canCreate={canCreate}
          draggingBookingId={drag?.active ? drag.bookingId : null}
          dropPreview={dropPreview}
          columnsRef={columnsRef}
          onSlotClick={handleSlotClick}
          onBlockPointerDown={handleBlockPointerDown}
          onBlockClick={handleBlockClick}
        />
      )}

      {hasUnassigned && (
        <UnassignedTray
          bookings={unassigned}
          draggingBookingId={drag?.active ? drag.bookingId : null}
          isDragActive={isDragActive}
          trayRef={trayRef}
          onPointerDown={handleTrayPointerDown}
        />
      )}

      <BookingInspector
        segment={inspectedSegment}
        selectedDate={selectedDate}
        bookingDuration={bookingDuration}
        openWindows={openWindows}
        isCancelling={isUpdatingStatus}
        isConfirming={isUpdatingStatus}
        canUpdate={canUpdate}
        onCancelBooking={handleCancelBooking}
        onConfirmBooking={handleConfirmBooking}
        onRescheduleDate={handleRescheduleDate}
        onClose={() => setInspectedId(null)}
      />

      <CreateBookingModal
        key={`${modalOpen}-${selectedDate.format("YYYY-MM-DD")}-${
          pendingSlot ? `${pendingSlot.table}-${pendingSlot.row}` : "any"
        }`}
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setPendingSlot(null);
        }}
        restaurantId={restaurantId}
        onSubmitDraft={handleSubmitDraft}
        hideDateTime
        bookingDate={selectedDate}
        slotHint={
          pendingSlot
            ? t("tableSchedule.newBookingAt", {
                table: pendingSlot.table,
                time: slotLabelForRow(pendingSlot.row),
                // Dashes, not slashes: i18next escapes "/" in interpolation and
                // the app cannot safely disable that globally (CustomText
                // renders translated labels through dangerouslySetInnerHTML).
                date: selectedDate.format("DD-MM-YYYY"),
              })
            : undefined
        }
      />

      <SnackBar
        open={!!errorMessage}
        onClose={() => setErrorMessage(null)}
        message={errorMessage ?? ""}
        severity="error"
      />
    </Box>
  );
};
